import { useState } from 'react';
import { channelsAPI, formatApiError } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { AlertTriangle, Bot, PauseCircle } from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';

/**
 * Si el canal recibe mensajes, y qué agentes lo atienden.
 *
 * **Por qué (2026-10-19).** Diego creó un canal de Teams para tickets@ sin dárselo a ningún agente,
 * y el relevador contestó igual: un agente sin canales marcados atendía todos. Ahora un canal lo
 * atienden solo los agentes que lo tienen marcado, y uno que nadie tiene marcado no se atiende.
 * Esto lo dice acá, para que no sea una trampa silenciosa.
 *
 * Además, pausar un canal solo se podía por la API: la pantalla no tenía cómo. Pausado, el canal
 * no recibe nada; lo que sale (respuestas y avisos) no se corta.
 */
const TIPOS_CON_AGENTE = ['email', 'teams', 'whatsapp'];

export const nadieLoAtiende = (ch) =>
  ch?.is_active !== false
  && TIPOS_CON_AGENTE.includes(ch?.channel_type)
  && Array.isArray(ch?.agentes_que_atienden)
  && ch.agentes_que_atienden.length === 0;

export default function AtencionDelCanal({ channel, onChannelUpdated }) {
  const [guardando, setGuardando] = useState(false);
  const activo = channel?.is_active !== false;
  const agentes = channel?.agentes_que_atienden;
  const conAgente = TIPOS_CON_AGENTE.includes(channel?.channel_type);

  const cambiar = async (valor) => {
    setGuardando(true);
    try {
      const res = await channelsAPI.update(channel.channel_id, { is_active: valor });
      onChannelUpdated({ ...res.data, agentes_que_atienden: agentes });
      toast.success(valor ? 'El canal vuelve a recibir mensajes' : 'Canal pausado: no recibe mensajes');
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo cambiar el canal');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="border border-brand-border rounded-sm p-4 space-y-3" data-testid="atencion-del-canal">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h4 className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1.5">
            Atención del canal <HelpButton section="channels_atencion" />
          </h4>
          <p className="text-xs text-zinc-500">
            {activo
              ? 'Recibe mensajes.'
              : 'Pausado: no recibe mensajes. Lo que sale (respuestas y avisos) sigue saliendo.'}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600 shrink-0">
          {activo ? 'Activo' : 'Pausado'}
          <Switch
            data-testid="canal-activo-switch"
            checked={activo}
            disabled={guardando}
            onCheckedChange={cambiar}
          />
        </label>
      </div>

      {conAgente && Array.isArray(agentes) && (
        agentes.length > 0 ? (
          <p className="text-xs text-zinc-600 flex items-start gap-1.5">
            <Bot className="w-3.5 h-3.5 mt-0.5 text-zinc-400 shrink-0" />
            <span>Lo atienden: <span className="font-mono">{agentes.join(', ')}</span></span>
          </p>
        ) : activo ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 flex items-start gap-1.5"
            data-testid="canal-sin-agente">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              <strong>Ningún agente lo atiende.</strong> Los mensajes que lleguen se descartan sin responder.
              Para atenderlo, marcalo en Agentes → (el agente) → Canales.
            </span>
          </p>
        ) : (
          <p className="text-xs text-zinc-500 flex items-start gap-1.5">
            <PauseCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Ningún agente lo tiene marcado.</span>
          </p>
        )
      )}
    </div>
  );
}
